import { useEffect, useMemo, useState } from 'react';
import ForecastLineChart, {
  formatCompactAxis,
  formatCountAxis,
} from './ForecastLineChart';
import SensitivityStrip from './SensitivityStrip';
import {
  computeCashRecoveryMonths,
  computeMonthsToProfitFromBand,
  formatMoney,
} from '../model/projectForecast';
import { FORECAST_PLAN_KEYS } from '../model/funnel';
import { SCENARIO_KEYS, SCENARIO_LABELS } from '../model/scenarios';
import {
  buildForecastCsv,
  buildForecastSummaryMarkdown,
  downloadForecastCsv,
} from '../model/exportForecastCsv';

const BAND_TABS = [
  { id: 'p10', label: 'P10 (downside)' },
  { id: 'p50', label: 'P50 (median)' },
  { id: 'p90', label: 'P90 (upside)' },
];

const BAND_STORAGE_KEY = 'ffc.admin.forecast.resultsBand';
const PLAN_LABELS = {
  basic: 'Basic',
  premium: 'Premium',
  enterprise: 'Enterprise',
};

/** Bridge table: compact for large $, 2dp only when small. */
function formatTableMoney(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1000) return formatCompactAxis(n);
  return formatMoney(n);
}

function formatTableLogos(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1000) return formatCountAxis(n);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function pickLogosByPlan(month, band) {
  const key =
    band === 'p10'
      ? 'logosByPlanP10'
      : band === 'p90'
        ? 'logosByPlanP90'
        : 'logosByPlanP50';
  return month?.[key] ?? month?.logosByPlan ?? {};
}

function pickBand(month, band) {
  if (band === 'p10') {
    return {
      mrr: month.mrrP10 ?? month.endMrr,
      profit: month.profitP10 ?? month.monthlyProfit,
      logos: month.logosP10 ?? month.endLogos,
      logosByPlan: pickLogosByPlan(month, 'p10'),
      bankLinkedLogos: month.bankLinkedLogosP10 ?? month.bankLinkedLogos ?? 0,
      cash: month.cashP10 ?? month.cash,
      signups: month.signupsP10 ?? month.signups,
      aiCogsMo: month.aiCogsMoP10 ?? month.aiCogsMo,
      aiPackRevenue: month.aiPackRevenueP10 ?? month.aiPackRevenue,
      variableCogsMo: month.variableCogsMoP10 ?? month.variableCogsMo,
      plaidCogsMo: month.plaidCogsMoP10 ?? month.plaidCogsMo,
      stripeCogsMo: month.stripeCogsMoP10 ?? month.stripeCogsMo,
      marketingSpend: month.marketingSpend,
      convRate: month.convRateP10 ?? month.convRate,
      bankLinkRate: month.bankLinkRate,
      eventLabels: month.eventLabels,
    };
  }
  if (band === 'p90') {
    return {
      mrr: month.mrrP90 ?? month.endMrr,
      profit: month.profitP90 ?? month.monthlyProfit,
      logos: month.logosP90 ?? month.endLogos,
      logosByPlan: pickLogosByPlan(month, 'p90'),
      bankLinkedLogos: month.bankLinkedLogosP90 ?? month.bankLinkedLogos ?? 0,
      cash: month.cashP90 ?? month.cash,
      signups: month.signupsP90 ?? month.signups,
      aiCogsMo: month.aiCogsMoP90 ?? month.aiCogsMo,
      aiPackRevenue: month.aiPackRevenueP90 ?? month.aiPackRevenue,
      variableCogsMo: month.variableCogsMoP90 ?? month.variableCogsMo,
      plaidCogsMo: month.plaidCogsMoP90 ?? month.plaidCogsMo,
      stripeCogsMo: month.stripeCogsMoP90 ?? month.stripeCogsMo,
      marketingSpend: month.marketingSpend,
      convRate: month.convRateP90 ?? month.convRate,
      bankLinkRate: month.bankLinkRate,
      eventLabels: month.eventLabels,
    };
  }
  return {
    mrr: month.mrrP50 ?? month.endMrr,
    profit: month.profitP50 ?? month.monthlyProfit,
    logos: month.logosP50 ?? month.endLogos,
    logosByPlan: pickLogosByPlan(month, 'p50'),
    bankLinkedLogos: month.bankLinkedLogosP50 ?? month.bankLinkedLogos ?? 0,
    cash: month.cashP50 ?? month.cash,
    signups: month.signupsP50 ?? month.signups,
    aiCogsMo: month.aiCogsMoP50 ?? month.aiCogsMo,
    aiPackRevenue: month.aiPackRevenueP50 ?? month.aiPackRevenue,
    variableCogsMo: month.variableCogsMoP50 ?? month.variableCogsMo,
    plaidCogsMo: month.plaidCogsMoP50 ?? month.plaidCogsMo,
    stripeCogsMo: month.stripeCogsMoP50 ?? month.stripeCogsMo,
    marketingSpend: month.marketingSpend,
    convRate: month.convRateP50 ?? month.convRate,
    bankLinkRate: month.bankLinkRate,
    eventLabels: month.eventLabels,
  };
}

function pickRunway(projection, band) {
  if (band === 'p10') return projection.runwayP10 ?? projection.runwayMonths;
  if (band === 'p90') return projection.runwayP90 ?? projection.runwayMonths;
  return projection.runwayMonths;
}

function pickMonthsToProfit(projection, band) {
  // Derive from the band profit series so KPI matches the bridge / charts.
  if (projection?.months?.length) {
    return computeMonthsToProfitFromBand(projection.months, band);
  }
  if (band === 'p10') return projection.monthsToProfitP10 ?? projection.monthsToProfit;
  if (band === 'p90') return projection.monthsToProfitP90 ?? projection.monthsToProfit;
  return projection.monthsToProfitP50 ?? projection.monthsToProfit;
}

function loadStoredBand() {
  try {
    const v = sessionStorage.getItem(BAND_STORAGE_KEY);
    if (v === 'p10' || v === 'p50' || v === 'p90') return v;
  } catch {
    /* ignore */
  }
  return 'p50';
}

function formatMonthsToProfit(value) {
  if (value == null) return 'Never in horizon';
  if (value === 0) return 'Now';
  return `${value} mo`;
}

function formatCashRecovery(value) {
  if (value == null) return 'Never in horizon';
  if (value === 0) return 'No drawdown';
  return `${value} mo`;
}

function actualsToChartPoints(syncedActuals) {
  const series = syncedActuals?.mrrSeries || [];
  return series.map((row, index) => ({ label: row.label, value: row.mrr, index }));
}

export default function ResultsPanel({
  projection,
  allProjections,
  compareMode,
  activeKey,
  activePack,
  syncedActuals,
  onPatchPack,
  scenarioAsOf,
  mcPending = false,
}) {
  const { months, mc, runs } = projection;
  const [viewBand, setViewBand] = useState(loadStoredBand);
  const activeBand = mc ? viewBand : 'p50';
  const bandLabel = BAND_TABS.find((t) => t.id === activeBand)?.label || 'P50';

  useEffect(() => {
    try {
      sessionStorage.setItem(BAND_STORAGE_KEY, viewBand);
    } catch {
      /* ignore */
    }
  }, [viewBand]);

  const bandRows = useMemo(
    () => months.map((m) => ({ month: m, band: pickBand(m, activeBand) })),
    [months, activeBand]
  );

  const firstBand = bandRows[0]?.band;
  const lastBand = bandRows[bandRows.length - 1]?.band;
  const runway = pickRunway(projection, activeBand);
  const monthsToProfit = pickMonthsToProfit(projection, activeBand);
  const openingCashValue =
    activePack?.assumptions?.startingCash ?? projection?.openingCash ?? firstBand?.cash;
  const openingCash = Number.isFinite(Number(openingCashValue))
    ? Number(openingCashValue)
    : undefined;
  const cashRecoveryFromBand = useMemo(
    () =>
      computeCashRecoveryMonths([
        openingCash,
        ...bandRows.map(({ band }) => band.cash),
      ]),
    [openingCash, bandRows]
  );
  const cashRecovery = cashRecoveryFromBand;

  const showRibbon = mc && activeBand === 'p50';
  const actualsAligned = actualsToChartPoints(syncedActuals);
  const modelLabels = months.map((month) => month.label);
  const priorActualLabels = (syncedActuals?.mrrSeries || [])
    .slice(0, -1)
    .map((row) => row.label);
  const mrrXLabels =
    priorActualLabels.length > 0
      ? [...priorActualLabels, ...modelLabels]
      : undefined;

  const mrrSeries = bandRows.map(({ month, band }) => ({
    label: month.label,
    value: band.mrr,
    p10: month.mrrP10,
    p90: month.mrrP90,
  }));
  const cashSeries = bandRows.map(({ month, band }) => ({
    label: month.label,
    value: band.cash,
    p10: month.cashP10,
    p90: month.cashP90,
  }));
  const logosSeries = bandRows.map(({ month, band }) => ({
    label: month.label,
    value: band.logos,
    p10: month.logosP10,
    p90: month.logosP90,
    tooltipRows: FORECAST_PLAN_KEYS.map((plan) => ({
      label: `${PLAN_LABELS[plan]} logos`,
      value: band.logosByPlan?.[plan] ?? 0,
    })),
  }));
  const lastActualMrr =
    syncedActuals?.mrrSeries?.[syncedActuals.mrrSeries.length - 1]?.mrr;
  const actualDeltaPct =
    firstBand?.mrr > 0 && Number(lastActualMrr) > 0
      ? ((firstBand.mrr - lastActualMrr) / lastActualMrr) *
        100
      : null;

  const handleExportCsv = () => {
    const csv = buildForecastCsv(
      months,
      (m) => pickBand(m, activeBand),
      { bandLabel: activeBand }
    );
    downloadForecastCsv(csv, `forecast-bridge-${activeBand}.csv`);
  };

  const handleCopySummary = async () => {
    const md = buildForecastSummaryMarkdown({
      bandLabel,
      openingMrr: firstBand?.mrr,
      horizonMrr: lastBand?.mrr,
      logos: lastBand?.logos,
      runway,
      monthsToProfit,
      cashRecovery,
      asOf: scenarioAsOf,
    });
    try {
      await navigator.clipboard.writeText(md);
    } catch {
      window.prompt('Copy summary:', md);
    }
  };

  const deepPatchPack = (patch) => {
    if (!onPatchPack) return;
    onPatchPack({
      assumptions: patch.assumptions
        ? { ...activePack.assumptions, ...patch.assumptions }
        : undefined,
      channels: patch.channels
        ? {
            ...activePack.channels,
            ...patch.channels,
            paid: { ...activePack.channels?.paid, ...patch.channels.paid },
            organic: { ...activePack.channels?.organic, ...patch.channels.organic },
          }
        : undefined,
      cohorts: patch.cohorts
        ? { ...activePack.cohorts, ...patch.cohorts }
        : undefined,
    });
  };

  return (
    <div className="economics-results">
      {mcPending && (
        <p className="module-status economics-callout" role="status">
          Updating Monte Carlo bands…
        </p>
      )}
      {mc && !mcPending && (
        <p className="module-status economics-callout">
          Monte Carlo · {runs} runs · page view follows selected band. Each run draws a growth
          regime (fall flat / traction / explode) plus conversion/churn/CAC noise. P50 is the
          cross-run median (not “Traction-only”). Pure Explode path: Growth tab or Bull pack.
        </p>
      )}

      <p className="chart-hint economics-muted">
        Illustrative — calibrate to Actuals. Reference bridge uses the active Growth preset;
        band charts use Monte Carlo percentiles when enabled.
      </p>

      {mc && (
        <nav className="economics-tabs forecast-tabs" aria-label="Monte Carlo band">
          {BAND_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={
                activeBand === tab.id ? 'economics-tab economics-tab-active' : 'economics-tab'
              }
              onClick={() => setViewBand(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      )}

      {mc && (
        <p className="chart-hint">
          Viewing <strong>{bandLabel}</strong> — KPIs, charts, and bridge outcomes.
        </p>
      )}

      <div className="economics-preset-row">
        <button type="button" className="secondary-button" onClick={handleExportCsv}>
          Export CSV
        </button>
        <button type="button" className="secondary-button" onClick={handleCopySummary}>
          Copy summary
        </button>
      </div>

      <div className="kpi-grid">
        <article className="kpi-card">
          <p className="kpi-label">MRR (now)</p>
          <p className="kpi-value">{formatTableMoney(firstBand?.mrr)}</p>
          {actualDeltaPct != null && (
            <p className="kpi-hint">
              vs actual: {actualDeltaPct >= 0 ? '+' : ''}
              {actualDeltaPct.toFixed(1)}%
            </p>
          )}
        </article>
        <article className="kpi-card">
          <p className="kpi-label">Logos (now)</p>
          <p className="kpi-value">{formatTableLogos(firstBand?.logos)}</p>
        </article>
        <article className="kpi-card">
          <p className="kpi-label">MRR (horizon)</p>
          <p className="kpi-value">{formatTableMoney(lastBand?.mrr)}</p>
        </article>
        <article className="kpi-card">
          <p className="kpi-label">Cash runway ({activeBand.toUpperCase()})</p>
          <p className="kpi-value">{runway ?? '—'} mo</p>
        </article>
        <article className="kpi-card">
          <p className="kpi-label">Months to profit ({activeBand.toUpperCase()})</p>
          <p className="kpi-value">{formatMonthsToProfit(monthsToProfit)}</p>
          <p className="kpi-hint">First month with positive ops profit</p>
        </article>
        <article className="kpi-card">
          <p className="kpi-label">Cash recovery ({activeBand.toUpperCase()})</p>
          <p className="kpi-value">{formatCashRecovery(cashRecovery)}</p>
          <p className="kpi-hint">Back to opening cash after drawdown</p>
        </article>
      </div>

      {activePack && onPatchPack && (
        <SensitivityStrip
          pack={activePack}
          band={activeBand}
          onApply={deepPatchPack}
        />
      )}

      {compareMode === 'all' && allProjections && (
        <section className="chart-card">
          <h2>Scenario compare (deterministic horizon)</h2>
          <p className="chart-hint economics-muted">
            Scenario comparison uses each pack&apos;s deterministic path. Monte Carlo bands belong
            to the active scenario only.
          </p>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Scenario</th>
                  <th>Logos</th>
                  {FORECAST_PLAN_KEYS.map((plan) => (
                    <th key={`scenario-${plan}`}>{PLAN_LABELS[plan]}</th>
                  ))}
                  <th>MRR</th>
                  <th>Cash</th>
                  <th>Runway</th>
                  <th>To profit</th>
                </tr>
              </thead>
              <tbody>
                {SCENARIO_KEYS.map((key) => {
                  const proj = allProjections[key];
                  const m = proj?.months?.[proj.months.length - 1];
                  const band = m ? pickBand(m, 'p50') : null;
                  return (
                    <tr key={key} className={activeKey === key ? 'forecast-row-active' : ''}>
                      <td>{SCENARIO_LABELS[key]}</td>
                      <td>{formatTableLogos(band?.logos)}</td>
                      {FORECAST_PLAN_KEYS.map((plan) => (
                        <td key={`${key}-${plan}`}>
                          {formatTableLogos(band?.logosByPlan?.[plan])}
                        </td>
                      ))}
                      <td>{formatTableMoney(band?.mrr)}</td>
                      <td>{formatTableMoney(band?.cash)}</td>
                      <td>{pickRunway(proj || {}, 'p50') ?? '—'} mo</td>
                      <td>{formatMonthsToProfit(pickMonthsToProfit(proj || {}, 'p50'))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="forecast-charts">
        <section className="chart-card">
          <h2>MRR · {bandLabel}</h2>
          <ForecastLineChart
            series={mrrSeries}
            ribbon={showRibbon}
            actuals={actualsAligned}
            xLabels={mrrXLabels}
            seriesStartIndex={priorActualLabels.length}
            formatValue={formatCompactAxis}
            emptyMessage="No projection data."
          />
        </section>
        <section className="chart-card">
          <h2>Logos · {bandLabel}</h2>
          <ForecastLineChart
            series={logosSeries}
            ribbon={showRibbon}
            formatValue={formatCountAxis}
            emptyMessage="No projection data."
          />
        </section>
        <section className="chart-card forecast-charts__cash">
          <h2>Cash · {bandLabel}</h2>
          <ForecastLineChart
            series={cashSeries}
            ribbon={showRibbon}
            referenceValue={openingCash}
            referenceLabel="Opening cash"
            formatValue={formatCompactAxis}
            emptyMessage="No projection data."
          />
        </section>
      </div>

      <section className="chart-card">
        <h2>MRR bridge table · {bandLabel}</h2>
        <p className="chart-hint economics-muted">
          <strong>Reference path:</strong> Prev / New / Expansion / Contraction / Churn / Shock /
          End MRR. <strong>Band ({bandLabel}):</strong> MRR / Monthly profit / Logos / Cash.
          Paid-plan logo columns use the evolving plan mix (Basic→Premium / Premium→Enterprise
          upgrades after month 0); they are an allocation view, not separate plan-specific
          cohorts. Enterprise stays 0 unless opening mix or Premium→Enterprise is above 0.
        </p>

        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Month</th>
                <th colSpan={7} className="forecast-colgroup-ref">
                  Reference path
                </th>
                <th colSpan={8} className="forecast-colgroup-band">
                  {bandLabel}
                </th>
              </tr>
              <tr>
                <th>Month</th>
                <th>Prev MRR</th>
                <th>+ New</th>
                <th>+ Expansion</th>
                <th>− Contraction</th>
                <th>− Churn</th>
                <th>Shock</th>
                <th>End MRR</th>
                <th>MRR</th>
                <th>Monthly profit</th>
                <th>Logos</th>
                {FORECAST_PLAN_KEYS.map((plan) => (
                  <th key={`bridge-${plan}`}>{PLAN_LABELS[plan]} logos</th>
                ))}
                <th>Bank-linked logos</th>
                <th>Cash</th>
              </tr>
            </thead>
            <tbody>
              {bandRows.map(({ month: m, band }) => (
                <tr key={m.monthIndex}>
                  <td>{m.label}</td>
                  <td>{formatTableMoney(m.prevMrr)}</td>
                  <td>{formatTableMoney(m.newMrr)}</td>
                  <td>{formatTableMoney(m.expansion)}</td>
                  <td>{formatTableMoney(m.contraction)}</td>
                  <td>{formatTableMoney(m.churnMrr)}</td>
                  <td>{formatTableMoney(m.shock)}</td>
                  <td>{formatTableMoney(m.bridgeEndMrr ?? m.endMrr)}</td>
                  <td>{formatTableMoney(band.mrr)}</td>
                  <td>{formatTableMoney(band.profit)}</td>
                  <td>{formatTableLogos(band.logos)}</td>
                  {FORECAST_PLAN_KEYS.map((plan) => (
                    <td key={`${m.monthIndex}-${plan}`}>
                      {formatTableLogos(band.logosByPlan?.[plan])}
                    </td>
                  ))}
                  <td>{formatTableLogos(band.bankLinkedLogos)}</td>
                  <td>{formatTableMoney(band.cash)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
